import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabaseAdmin } from '../../config/supabase';
import { Plus, Trash2, Loader2, Edit2, Save, X, Search, Users, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from '../../components/ui/use-toast';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

// Types for profession/category items
interface ProfessionItem {
  id: number | string;
  name: string;
  description?: string;
  category?: string;
  is_accepted?: boolean;
  usage_count?: number;
}

// Table configuration for different profession types
type TableType = 'professions' | 'craft_professions' | 'hospitality_categories' | 'professionals_categories' | 'business_types';

interface TableConfig {
  name: string;
  displayName: string;
  singularName: string;
  usageTable: string;
  usageColumn: string;
  hasCategory: boolean;
  categoryOptions?: string[];
}

const TABLE_CONFIGS: Record<TableType, TableConfig> = {
  professions: {
    name: 'professions',
    displayName: 'Service Professions',
    singularName: 'Profession',
    usageTable: 'traders',
    usageColumn: 'profession_id',
    hasCategory: true,
    categoryOptions: [
      'Building & Construction',
      'Interiors & Renovation',
      'Plumbing & Heating',
      'Home Maintenance',
      'Garden & Outdoors',
      'Cleaning & Logistics',
      'Motors',
      'Health & Lifestyle',
      'Technology & IT',
      'Creative & Media',
    ],
  },
  craft_professions: {
    name: 'craft_professions',
    displayName: 'Craft Professions',
    singularName: 'Profession',
    usageTable: 'crafties',
    usageColumn: 'profession_id',
    hasCategory: true,
    categoryOptions: ['Other'],
  },
  hospitality_categories: {
    name: 'hospitality_categories',
    displayName: 'Hospitality Professions',
    singularName: 'Profession',
    usageTable: 'hospitality',
    usageColumn: 'profession_id',
    hasCategory: true,
    categoryOptions: ['Restaurant', 'Accommodation', 'Entertainment', 'Food Service', 'Bar & Pub', 'Cafe', 'Other'],
  },
  professionals_categories: {
    name: 'professionals_categories',
    displayName: 'Professionals Professions',
    singularName: 'Profession',
    usageTable: 'professionals',
    usageColumn: 'profession_id',
    hasCategory: true,
    categoryOptions: ['Consulting', 'Legal & Finance', 'Medical & Health', 'Education & Coaching', 'Creative & Media', 'Other'],
  },
  business_types: {
    name: 'business_types',
    displayName: 'Business Types',
    singularName: 'Business Type',
    usageTable: 'traders',
    usageColumn: 'business_type_id',
    hasCategory: false,
  },
};

const ProfessionManager: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { adminUser } = useAdminAuth();
  
  // State management
  const [items, setItems] = useState<ProfessionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState<number | string | null>(null);
  const [editForm, setEditForm] = useState<Partial<ProfessionItem>>({});
  const [isAdding, setIsAdding] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', description: '', category: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [sortField, setSortField] = useState<'name' | 'category' | 'usage_count'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Determine current table from URL
  const queryParams = new URLSearchParams(location.search);
  const tableParam = (queryParams.get('table') || 'professions') as TableType;
  const currentConfig = TABLE_CONFIGS[tableParam] || TABLE_CONFIGS.professions;

  const categoryOptions: string[] = useMemo(() => {
    if (!currentConfig.hasCategory) {
      return currentConfig.categoryOptions || [];
    }

    const dynamicCategories = Array.from(
      new Set(
        items
          .map(item => item.category)
          .filter((c): c is string => Boolean(c && c.trim()))
      )
    );

    if (dynamicCategories.length > 0) {
      return dynamicCategories;
    }

    return currentConfig.categoryOptions || [];
  }, [currentConfig, items]);

  // Fetch items with usage counts
  const fetchItems = useCallback(async () => {
    if (!adminUser) {
      toast({ title: 'Unauthorized', description: 'You do not have permission.', variant: 'destructive' });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch profession/category items
      const { data: itemsData, error: itemsError } = await supabaseAdmin
        .from(currentConfig.name)
        .select('*')
        .order('name');

      if (itemsError) throw itemsError;

      // Fetch usage counts from the related table
      const { data: usageData, error: usageError } = await supabaseAdmin
        .from(currentConfig.usageTable)
        .select(currentConfig.usageColumn);

      if (usageError) {
        console.warn('Could not fetch usage data:', usageError);
      }

      // Calculate usage counts for each item
      const usageCounts: Record<string, number> = {};
      if (usageData) {
        usageData.forEach((row: any) => {
          const id = String(row[currentConfig.usageColumn]);
          usageCounts[id] = (usageCounts[id] || 0) + 1;
        });
      }

      // Merge usage counts with items
      const itemsWithUsage = (itemsData || []).map(item => ({
        ...item,
        usage_count: usageCounts[String(item.id)] || 0,
      }));

      setItems(itemsWithUsage);
    } catch (error) {
      console.error(`Error fetching ${currentConfig.name}:`, error);
      toast({ title: 'Error', description: `Failed to load ${currentConfig.displayName}`, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [adminUser, currentConfig]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems, tableParam]);

  // Add new item
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItem.name.trim()) return;

    try {
      setIsAdding(true);
      const insertData: any = {
        name: newItem.name.trim(),
        description: newItem.description.trim() || null,
        is_accepted: true, // Admin-added items are auto-approved
      };

      if (currentConfig.hasCategory && newItem.category) {
        insertData.category = newItem.category;
      }

      const { data, error } = await supabaseAdmin
        .from(currentConfig.name)
        .insert(insertData)
        .select();

      if (error) throw error;

      setItems(prev => [...prev, { ...data[0], usage_count: 0 }]);
      setNewItem({ name: '', description: '', category: '' });
      setShowAddForm(false);
      toast({ title: 'Success', description: `${currentConfig.singularName} added successfully` });
    } catch (error) {
      console.error('Error adding item:', error);
      toast({ title: 'Error', description: `Failed to add ${currentConfig.singularName}`, variant: 'destructive' });
    } finally {
      setIsAdding(false);
    }
  };

  // Start editing an item
  const startEdit = (item: ProfessionItem) => {
    setEditingId(item.id);
    setEditForm({
      name: item.name,
      description: item.description || '',
      category: item.category || '',
      is_accepted: item.is_accepted,
    });
  };

  // Save edited item
  const saveEdit = async () => {
    if (!editingId || !editForm.name?.trim()) return;

    try {
      const updateData: any = {
        name: editForm.name.trim(),
        description: editForm.description?.trim() || null,
      };

      if (currentConfig.hasCategory) {
        updateData.category = editForm.category || null;
      }

      if (editForm.is_accepted !== undefined) {
        updateData.is_accepted = editForm.is_accepted;
      }

      const { error } = await supabaseAdmin
        .from(currentConfig.name)
        .update(updateData)
        .eq('id', editingId);

      if (error) throw error;

      setItems(prev => prev.map(item => 
        item.id === editingId ? { ...item, ...updateData } : item
      ));
      setEditingId(null);
      setEditForm({});
      toast({ title: 'Success', description: `${currentConfig.singularName} updated successfully` });
    } catch (error) {
      console.error('Error updating item:', error);
      toast({ title: 'Error', description: `Failed to update ${currentConfig.singularName}`, variant: 'destructive' });
    }
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  // Delete item
  const handleDelete = async (item: ProfessionItem) => {
    if (item.usage_count && item.usage_count > 0) {
      toast({
        title: 'Cannot Delete',
        description: `This ${currentConfig.singularName.toLowerCase()} is used by ${item.usage_count} business(es). Remove associations first.`,
        variant: 'destructive',
      });
      return;
    }

    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;

    try {
      const { error } = await supabaseAdmin
        .from(currentConfig.name)
        .delete()
        .eq('id', item.id);

      if (error) throw error;

      setItems(prev => prev.filter(i => i.id !== item.id));
      toast({ title: 'Success', description: `${currentConfig.singularName} deleted successfully` });
    } catch (error) {
      console.error('Error deleting item:', error);
      toast({ title: 'Error', description: `Failed to delete ${currentConfig.singularName}`, variant: 'destructive' });
    }
  };

  // Toggle approval status
  const toggleApproval = async (item: ProfessionItem) => {
    try {
      const newStatus = !item.is_accepted;
      const { error } = await supabaseAdmin
        .from(currentConfig.name)
        .update({ is_accepted: newStatus })
        .eq('id', item.id);

      if (error) throw error;

      setItems(prev => prev.map(i => 
        i.id === item.id ? { ...i, is_accepted: newStatus } : i
      ));
      toast({ title: 'Success', description: `${currentConfig.singularName} ${newStatus ? 'approved' : 'unapproved'}` });
    } catch (error) {
      console.error('Error toggling approval:', error);
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  // Filter and sort items
  const filteredItems = items
    .filter(item => {
      const query = searchQuery.toLowerCase();
      return (
        item.name.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.category?.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      let comparison = 0;
      if (sortField === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (sortField === 'category') {
        const aCat = (a.category || '').toLowerCase();
        const bCat = (b.category || '').toLowerCase();
        comparison = aCat.localeCompare(bCat);
      } else if (sortField === 'usage_count') {
        comparison = (a.usage_count || 0) - (b.usage_count || 0);
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  // Toggle sort
  const toggleSort = (field: 'name' | 'category' | 'usage_count') => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Navigate to different table
  const navigateToTable = (table: TableType) => {
    navigate(`/admin/professions?table=${table}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin w-12 h-12 text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">{currentConfig.displayName}</h1>
        
        {/* Search */}
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 border-b pb-4">
        {Object.entries(TABLE_CONFIGS).map(([key, config]) => (
          <button
            key={key}
            onClick={() => navigateToTable(key as TableType)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              tableParam === key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {config.displayName}
          </button>
        ))}
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="text-2xl font-bold text-blue-600">{items.length}</div>
          <div className="text-sm text-gray-600">Total {currentConfig.displayName}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="text-2xl font-bold text-green-600">
            {items.filter(i => i.is_accepted !== false).length}
          </div>
          <div className="text-sm text-gray-600">Approved</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="text-2xl font-bold text-yellow-600">
            {items.filter(i => i.is_accepted === false).length}
          </div>
          <div className="text-sm text-gray-600">Pending Approval</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="text-2xl font-bold text-purple-600">
            {items.reduce((sum, i) => sum + (i.usage_count || 0), 0)}
          </div>
          <div className="text-sm text-gray-600">Total Usage</div>
        </div>
      </div>

      {/* Add New Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {showAddForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showAddForm ? 'Cancel' : `Add ${currentConfig.singularName}`}
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Add New {currentConfig.singularName}</h3>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={newItem.name}
                  onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              {currentConfig.hasCategory && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category Type</label>
                  <select
                    value={newItem.category}
                    onChange={(e) => setNewItem(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select category</option>
                    {categoryOptions.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={newItem.description}
                onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                rows={2}
              />
            </div>
            <button
              type="submit"
              disabled={isAdding}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {isAdding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add {currentConfig.singularName}
            </button>
          </form>
        </div>
      )}

      {/* Items Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">ID</th>
                <th 
                  className="text-left p-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => toggleSort('name')}
                >
                  <div className="flex items-center gap-1">
                    Name
                    {sortField === 'name' && (sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                  </div>
                </th>
                {currentConfig.hasCategory && (
                  <th
                    className="text-left p-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleSort('category')}
                  >
                    <div className="flex items-center gap-1">
                      Category
                      {sortField === 'category' && (sortDirection === 'asc' ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      ))}
                    </div>
                  </th>
                )}
                <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Description</th>
                <th 
                  className="text-left p-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => toggleSort('usage_count')}
                >
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    Usage
                    {sortField === 'usage_count' && (sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                  </div>
                </th>
                <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-right p-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  {/* ID */}
                  <td className="p-3 text-xs font-mono text-gray-500">{item.id}</td>
                  
                  {/* Name */}
                  <td className="p-3">
                    {editingId === item.id ? (
                      <input
                        type="text"
                        value={editForm.name || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <span className="font-medium text-gray-900">{item.name}</span>
                    )}
                  </td>
                  
                  {/* Category (if applicable) */}
                  {currentConfig.hasCategory && (
                    <td className="p-3">
                      {editingId === item.id ? (
                        <select
                          value={editForm.category || ''}
                          onChange={(e) => setEditForm(prev => ({ ...prev, category: e.target.value }))}
                          className="w-full px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select</option>
                          {categoryOptions.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-gray-600">{item.category || '-'}</span>
                      )}
                    </td>
                  )}
                  
                  {/* Description */}
                  <td className="p-3">
                    {editingId === item.id ? (
                      <input
                        type="text"
                        value={editForm.description || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                        className="w-full px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500"
                        placeholder="No description"
                      />
                    ) : (
                      <span className="text-gray-600 text-sm">{item.description || '-'}</span>
                    )}
                  </td>
                  
                  {/* Usage Count */}
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      (item.usage_count || 0) > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <Users className="w-3 h-3" />
                      {item.usage_count || 0}
                    </span>
                  </td>
                  
                  {/* Status */}
                  <td className="p-3">
                    <button
                      onClick={() => toggleApproval(item)}
                      className={`px-2 py-1 text-xs font-medium rounded-full transition-colors ${
                        item.is_accepted !== false
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                      }`}
                    >
                      {item.is_accepted !== false ? 'Approved' : 'Pending'}
                    </button>
                  </td>
                  
                  {/* Actions */}
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {editingId === item.id ? (
                        <>
                          <button
                            onClick={saveEdit}
                            className="p-2 text-green-600 hover:bg-green-50 rounded"
                            title="Save"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEdit(item)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item)}
                            className={`p-2 rounded ${
                              (item.usage_count || 0) > 0
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-red-600 hover:bg-red-50'
                            }`}
                            title={(item.usage_count || 0) > 0 ? 'Cannot delete: in use' : 'Delete'}
                            disabled={(item.usage_count || 0) > 0}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Empty State */}
        {filteredItems.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>No {currentConfig.displayName.toLowerCase()} found.</p>
            {searchQuery && <p className="text-sm mt-2">Try adjusting your search.</p>}
          </div>
        )}
      </div>

      {/* Results count */}
      <div className="text-sm text-gray-500">
        Showing {filteredItems.length} of {items.length} {currentConfig.displayName.toLowerCase()}
      </div>
    </div>
  );
};

export default ProfessionManager;
